import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import '../styles/group-creatives.css';

interface Creative {
  id: number;
  advertiser_id: string;
  display_name: string;
  creative_type: 'IMAGE' | 'HTML5' | 'VIDEO';
  ads_creative_id: string | null;
  review_status: string;
  width: number | null;
  height: number | null;
  has_file: boolean;
  has_thumbnail: boolean;
  preview_url: string | null;
}

interface GroupCreativesSectionProps {
  groupId: string | number;
  advertiserId: string;
  groupType: 'display' | 'video';
  assignedCreativeIds: number[];
  onCreativesChange: (creativeIds: number[]) => void;
  disabled?: boolean;
}

const GroupCreativesSection: React.FC<GroupCreativesSectionProps> = ({
  groupId,
  advertiserId,
  groupType,
  assignedCreativeIds,
  onCreativesChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [assignedCreatives, setAssignedCreatives] = useState<Creative[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  // Selection modal state - availableCreatives holds ALL creatives (loaded once)
  const [availableCreatives, setAvailableCreatives] = useState<Creative[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'IMAGE' | 'VIDEO' | 'HTML5'>('all');
  const [loadingAvailable, setLoadingAvailable] = useState(true);

  // Load ALL creatives once when component mounts or advertiserId changes
  useEffect(() => {
    loadAllCreatives();
  }, [advertiserId]);

  // Update assigned creatives when IDs or available list changes
  useEffect(() => {
    if (!loadingAvailable) {
      const assigned = availableCreatives.filter((c: Creative) => assignedCreativeIds.includes(c.id));
      setAssignedCreatives(assigned);
      setLoading(false);
    }
  }, [assignedCreativeIds, availableCreatives, loadingAvailable]);

  const loadAllCreatives = async () => {
    setLoadingAvailable(true);
    try {
      const response = await api.get(`/api/creatives?advertiser_id=${advertiserId}`);
      const allCreatives = response.data.creatives || [];
      setAvailableCreatives(allCreatives);
    } catch (err) {
      console.error('Failed to load creatives:', err);
    } finally {
      setLoadingAvailable(false);
    }
  };

  // Open modal - data is already preloaded, no fetch needed
  const openModal = () => {
    setModalOpen(true);
    setSelectedIds(new Set(assignedCreativeIds));
    setSearchQuery('');
    setTypeFilter('all');
  };

  // Close modal
  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    if (!modalOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, closeModal]);

  // Apply selection
  const applySelection = () => {
    onCreativesChange(Array.from(selectedIds));
    closeModal();
  };

  // Toggle creative selection
  const toggleCreativeSelection = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Remove creative from group
  const removeCreative = (id: number) => {
    onCreativesChange(assignedCreativeIds.filter(cid => cid !== id));
  };

  // Get allowed creative types based on group type
  const allowedTypes: Creative['creative_type'][] = groupType === 'video'
    ? ['VIDEO']
    : ['IMAGE', 'HTML5'];

  // Filter creatives - first by group type, then by user filters
  const creativesForGroupType = availableCreatives.filter(c => allowedTypes.includes(c.creative_type));

  const filteredCreatives = creativesForGroupType.filter(c => {
    const matchesSearch = c.display_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || c.creative_type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Get file URL
  const getFileUrl = (creative: Creative): string | null => {
    if (creative.has_file) {
      return `/api/creatives/${creative.id}/file`;
    }
    return creative.preview_url;
  };

  const getThumbnailUrl = (creative: Creative): string | null => {
    // Videos - only show if has thumbnail
    if (creative.creative_type === 'VIDEO') {
      if (creative.has_thumbnail) {
        return `/api/creatives/${creative.id}/thumbnail`;
      }
      return null; // Show placeholder for videos without thumbnail
    }
    // HTML5 - always show placeholder
    if (creative.creative_type === 'HTML5') {
      return null;
    }
    // Images - show actual image
    return getFileUrl(creative);
  };

  // Type icon
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'IMAGE':
        return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>;
      case 'VIDEO':
        return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>;
      case 'HTML5':
        return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>;
      default:
        return null;
    }
  };

  return (
    <div className="group-creatives-section">
      <div className="section-header">
        <h4 className="section-label">{t('creatives.sectionLabel')}</h4>
      </div>

      {loading ? (
        <div className="creatives-loading">
          <div className="loading-spinner" />
        </div>
      ) : assignedCreatives.length === 0 ? (
        <div
          className={`creatives-empty-clickable ${disabled ? 'disabled' : ''}`}
          onClick={disabled ? undefined : openModal}
        >
          <div className="empty-icon">
            {/* Default icon */}
            <svg className="icon-default" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5-7l-3 3.72L9 13l-3 4h12l-4-5z"/>
            </svg>
            {/* Hover icon */}
            <svg className="icon-hover" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
          </div>
          <div className="text-container">
            <p className="text-default">{t('creatives.noneAssigned')}</p>
            <p className="text-hover">{t('creatives.add')}</p>
          </div>
          <span className="subtext">{t('creatives.addHint')}</span>
        </div>
      ) : (
        <div className="file-grid">
          {assignedCreatives.map((creative, index) => (
            <div
              key={creative.id}
              className="file-card"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="file-card-preview">
                {getThumbnailUrl(creative) ? (
                  <img src={getThumbnailUrl(creative)!} alt={creative.display_name} />
                ) : (
                  <div className={`file-card-placeholder ${creative.creative_type.toLowerCase()}`}>
                    {getTypeIcon(creative.creative_type)}
                  </div>
                )}
                <div className="file-card-type">
                  {getTypeIcon(creative.creative_type)}
                </div>
                {!disabled && (
                  <button
                    className="file-card-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeCreative(creative.id);
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                )}
              </div>
              <div className="file-card-info">
                <span className="file-card-name" title={creative.display_name}>
                  {creative.display_name}
                </span>
                <span className="file-card-meta">
                  {creative.width && creative.height
                    ? `${creative.width} × ${creative.height}`
                    : creative.creative_type}
                </span>
              </div>
            </div>
          ))}

          {/* Add more card */}
          {!disabled && (
            <button className="add-more-card" onClick={openModal}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              <span>{t('creatives.addMore')}</span>
            </button>
          )}
        </div>
      )}

      {/* Modal Overlay */}
      {modalOpen && (
        <div className="creatives-modal-overlay" onClick={closeModal}>
          <div className="creatives-modal" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header">
              <h2>{t('creatives.addTitle')}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="modal-content">
              <>
                  {/* Search & Filter */}
                  <div className="creatives-toolbar">
                    <div className="search-input-wrapper">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                      </svg>
                      <input
                        type="text"
                        placeholder={t('creatives.search')}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                      />
                    </div>
                    {/* Type filter - only show for display groups (has multiple types) */}
                    {groupType === 'display' && (
                      <div className="type-filter">
                        {(['all', 'IMAGE', 'HTML5'] as const).map(type => (
                          <button
                            key={type}
                            className={`type-filter-btn ${typeFilter === type ? 'active' : ''}`}
                            onClick={() => setTypeFilter(type)}
                          >
                            {type === 'all' ? t('creatives.filterAll') : type}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selection info */}
                  <div className="selection-info">
                    <span>{t(groupType === 'video' ? 'creatives.selectedOfVideo' : 'creatives.selectedOfDisplay', { selected: selectedIds.size, total: creativesForGroupType.length })}</span>
                    {selectedIds.size !== assignedCreativeIds.length && (
                      <span className="selection-change">
                        {selectedIds.size > assignedCreativeIds.length
                          ? t('creatives.added', { count: selectedIds.size - assignedCreativeIds.length })
                          : t('creatives.removed', { count: assignedCreativeIds.length - selectedIds.size })
                        }
                      </span>
                    )}
                  </div>

                  {/* Creatives Grid */}
                  <div className="file-grid">
                    {loadingAvailable ? (
                      <div className="grid-loading">
                        <div className="loading-spinner" />
                      </div>
                    ) : filteredCreatives.length === 0 ? (
                      <div className="grid-empty">
                        <p>{t('creatives.noneFound')}</p>
                      </div>
                    ) : (
                      filteredCreatives.map(creative => {
                        const isSelected = selectedIds.has(creative.id);
                        const wasAssigned = assignedCreativeIds.includes(creative.id);

                        return (
                          <div
                            key={creative.id}
                            className={`file-card selectable ${isSelected ? 'selected' : ''} ${wasAssigned ? 'was-assigned' : ''}`}
                            onClick={() => toggleCreativeSelection(creative.id)}
                          >
                            <div className="file-card-checkbox">
                              {isSelected && (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                </svg>
                              )}
                            </div>
                            <div className="file-card-preview">
                              {getThumbnailUrl(creative) ? (
                                <img src={getThumbnailUrl(creative)!} alt={creative.display_name} />
                              ) : (
                                <div className={`file-card-placeholder ${creative.creative_type.toLowerCase()}`}>
                                  {getTypeIcon(creative.creative_type)}
                                </div>
                              )}
                              <div className="file-card-type">
                                {getTypeIcon(creative.creative_type)}
                              </div>
                            </div>
                            <div className="file-card-info">
                              <span className="file-card-name" title={creative.display_name}>
                                {creative.display_name}
                              </span>
                              <span className="file-card-meta">
                                {creative.width && creative.height
                                  ? `${creative.width} × ${creative.height}`
                                  : creative.creative_type}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeModal}>
                {t('edit.cancel')}
              </button>
              <button
                className="btn-apply-selection"
                onClick={applySelection}
                disabled={selectedIds.size === 0}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                {t('creatives.apply', { count: selectedIds.size })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupCreativesSection;
